/* Browser side of the public tracker. Prefers the same-origin proxy
   (src/setupProxy.js in development, api/track-proxy.js on Vercel) and falls
   back to the Supabase Edge Function directly if the proxy isn't reachable. */

export const TRACKING_LENGTH = 10;
const TRACK_URL = "/api/track-proxy";
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/* Arabic-Indic (٠-٩) and Persian (۰-۹) digits are what many customers type on
   an Arabic keyboard; fold them to ASCII and drop everything else. */
export function normalizeTrackingInput(raw) {
  return String(raw || "")
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/\D/g, "")
    .slice(0, TRACKING_LENGTH);
}

export const isValidTracking = (n) => /^[0-9]{10}$/.test(n);

/** Group digits for display (4-3-3): "1000 000 001". */
export function formatTracking(n) {
  const s = String(n || "");
  return [s.slice(0, 4), s.slice(4, 7), s.slice(7)].filter(Boolean).join(" ");
}

/**
 * @returns {Promise<{status: "found"|"not_found"|"invalid"|"rate_limited"|"error", shipment?: object, retryAfter?: number}>}
 */
const ANSWERS = ["found", "not_found", "invalid", "rate_limited"];

const camel = (k) => k.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
/* Deep snake_case → camelCase for plain objects; numeric keys ("0".."4" stage
   maps) and array items keep their shape. */
function camelize(v) {
  if (Array.isArray(v)) return v.map(camelize);
  if (v && typeof v === "object") {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [camel(k), camelize(x)]));
  }
  return v;
}

/* The Edge Function answers in snake_case (tracking_number, parcels_by_stage…);
   the UI reads camelCase. Already-camel payloads pass through unchanged. */
export function toShipment(raw) {
  if (!raw || typeof raw !== "object") return raw;
  return camelize(raw);
}

/* One attempt against one endpoint; null means "no usable answer, try the next". */
async function ask(url, headers, number, signal) {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify({ tracking_number: number }),
      cache: "no-store",
      signal,
    });
    const data = await res.json().catch(() => null);
    if (!data || !ANSWERS.includes(data.status)) return null;
    if (data.retry_after) data.retryAfter = data.retry_after;
    if (data.shipment) data.shipment = toShipment(data.shipment);
    return data;
  } catch (err) {
    if (err?.name === "AbortError") throw err;
    return null;
  }
}

export async function trackShipment(number, { signal } = {}) {
  if (!isValidTracking(number)) return { status: "invalid" };

  // 1) Same-origin proxy. 2) If that route isn't available on this deployment
  //    (missing env, older build, static host), call the Edge Function directly;
  //    it allows the site's origins.
  const viaProxy = await ask(TRACK_URL, {}, number, signal);
  if (viaProxy) return viaProxy;

  if (SUPABASE_URL && SUPABASE_ANON_KEY) {
    const direct = await ask(
      `${SUPABASE_URL.replace(/\/+$/, "")}/functions/v1/track`,
      { Authorization: `Bearer ${SUPABASE_ANON_KEY}`, apikey: SUPABASE_ANON_KEY },
      number,
      signal,
    );
    if (direct) return direct;
  }

  console.error("Tracking request failed on every route");
  return { status: "error" };
}
